import { useMemo, type ComponentProps } from 'react'
import { Card, Chip } from '@heroui/react'
import { branchHours } from '../lib/branchHours'
import { useNow } from '../lib/clock'
import { dayPart, firstName, homeFigures, homeTiles, tileBadges, type Tile, type TileBadge, type TileKey } from '../lib/homeTiles'
import type { Branch, Identity } from '../lib/types'
import { formatMoment, type StaffKey, type StaffLanguage } from '../lib/staffText'
import { useBranchLive } from '../lib/useBranch'
import { Icon, type IconName } from '../ui/icons'
import { Topbar } from '../ui/Chrome'
import { useSession } from '../ui/Session'
import { useStaffText } from '../ui/StaffText'
import { sectionsFor } from './Administration'
import { HomePreview } from './homePreviews'
import '../styles/home.css'

/**
 * Where an operator lands: a greeting, the branch now, and a card per app they may open,
 * work apps with a sample picture first. No kiosk or hall board here: an addressed screen claims its machine.
 */

const FACES: Record<TileKey, { name: StaffKey; icon: IconName }> = {
  counter: { name: 'entry.app.counter', icon: 'counter' },
  reception: { name: 'entry.app.reception', icon: 'reception' },
  bookings: { name: 'entry.app.appointments', icon: 'booking' },
  floor: { name: 'entry.card.floor', icon: 'floor' },
  reports: { name: 'entry.app.reports', icon: 'reports' },
  audit: { name: 'entry.tile.audit', icon: 'audit' },
  signage: { name: 'entry.app.signage', icon: 'play' },
  designs: { name: 'entry.app.designs', icon: 'designs' },
  devices: { name: 'entry.card.devices', icon: 'machine' },
  people: { name: 'entry.app.people', icon: 'people' },
  administration: { name: 'entry.card.administration', icon: 'admin' },
  organisations: { name: 'admin.section.organisations', icon: 'organisations' },
}

const GREETING = { morning: 'entry.home.morning', afternoon: 'entry.home.afternoon', evening: 'entry.home.evening' } as const

// A card's parts as spans: the card is a button, and a button holds phrasing content only.
const asSpan = (props: object) => <span {...(props as ComponentProps<'span'>)} />

/** The tiles this person may open; App uses the same list to skip Home for a person with one. */
export function tilesFor(identity: Identity): Tile[] {
  return homeTiles({
    permissions: identity.permissions,
    modules: identity.modules,
    administer: sectionsFor(identity).length > 0,
  })
}

export function Home({
  identity,
  onNavigate,
}: {
  identity: Identity
  onNavigate: (to: string) => void
  /** Signing out is on the top bar's account menu now. */
  onSignOut?: () => void
}) {
  const { lang, say } = useStaffText()
  const staff = identity.staff
  // The top bar's branch, so the line and the numbers are about the branch the person chose.
  const live = useBranchLive('home', { lang })
  // The owner with "All organisations" has no branch in view: no line, no figures.
  const branch = useSession()?.org?.needed ? null : live.branch

  const tiles = useMemo(() => tilesFor(identity), [identity])
  const state = live.state && branch && live.state.branchId === branch.id ? live.state : null
  const now = useNow()
  const figures = homeFigures(state, identity.permissions, staff.id, now)

  const badgeWords = (b: TileBadge) =>
    b.what === 'waiting'
      ? say('ui.home.waiting', { n: b.n })
      : b.what === 'over'
        ? say('ui.home.queuesOver', { n: b.n })
        : say('ui.home.requests', { n: b.n })

  const hour = Number(formatMoment(new Date(now), lang, { time: true, zone: branch?.timezone }).slice(0, 2))
  const greeting = say(GREETING[dayPart(hour)], { name: firstName(staff.displayName, staff.username) })

  const day = branch ? branchHours(branch, new Date(now)) : 'none'
  const open = branch ? hours(branch, day, lang, say) : ''
  const status = branch ? [branch.name, open].filter(Boolean) : []

  const workTiles = tiles.filter((t) => t.family === 'work')
  const rest = tiles.filter((t) => t.family !== 'work')
  const card = (tile: Tile) => {
    const face = FACES[tile.key]
    const badges = tileBadges(tile.key, figures)
    const name = say(face.name)
    const work = tile.family === 'work'
    const chips = badges.length > 0 && (
      <span className="ws-card__chips">
        {badges.map((b) => (
          <Chip key={b.what} size="sm" variant="soft" color={b.tone === 'over' ? 'warning' : 'default'} title={badgeWords(b)}>
            {b.tone === 'over' && <Icon name="alert" size={12} />}
            <Chip.Label>{b.n}</Chip.Label>
          </Chip>
        ))}
      </span>
    )
    const words = (
      <Card.Header render={asSpan} className="ws-card__words">
        <Card.Title render={asSpan}>{name}</Card.Title>
      </Card.Header>
    )
    return (
      <li key={tile.key}>
        <Card
          className={work ? 'ws-card ws-card--work' : 'ws-card ws-card--tool'}
          render={(props) => (
            <button
              {...(props as ComponentProps<'button'>)}
              type="button"
              data-key={tile.key}
              onClick={() => onNavigate(tile.to)}
              aria-label={[name, ...badges.map(badgeWords)].join(', ')}
            />
          )}
        >
          {work ? (
            <>
              <span className="ws-card__preview" aria-hidden="true">
                <HomePreview tile={tile.key} />
              </span>
              <span className="ws-card__copy">
                {words}
                {chips}
              </span>
            </>
          ) : (
            <>
              <span className="ws-card__icon" aria-hidden="true">
                <Icon name={face.icon} size={18} />
              </span>
              {words}
              {chips}
            </>
          )}
        </Card>
      </li>
    )
  }

  return (
    <div className="app">
      <Topbar branch={branch} branches={live.branches} onChooseBranch={live.chooseBranch} />
      <main className="page ws" aria-label={say('entry.home.title')}>
        <header className="ws-head">
          <h1 className="ws-head__title">{greeting}</h1>
          {status.length > 0 && (
            <p className="ws-status">
              <span className="ws-status__dot" data-state={dotState(day)} aria-hidden="true" />
              {status.map((part, i) => (
                <span key={i}>{part}</span>
              ))}
            </p>
          )}
        </header>

        {tiles.length === 0 && <p className="ws-none">{say('ui.home.nothing')}</p>}
        {workTiles.length > 0 && (
          <section className="ws-section" aria-labelledby="ws-operations">
            <h2 className="ws-section__title" id="ws-operations">
              {say('entry.home.operations')}
            </h2>
            <ul className="ws-cards">{workTiles.map(card)}</ul>
          </section>
        )}
        {rest.length > 0 && (
          <section className="ws-section" aria-labelledby="ws-management">
            <h2 className="ws-section__title" id="ws-management">
              {say('entry.home.management')}
            </h2>
            <ul className="ws-tools">{rest.map(card)}</ul>
          </section>
        )}
      </main>
    </div>
  )
}

/** Green open, grey closed, hollow when the branch keeps no hours. */
function dotState(day: ReturnType<typeof branchHours>): 'open' | 'closed' | 'unknown' {
  return day === 'none' ? 'unknown' : day === 'closed' ? 'closed' : 'open'
}

function hours(
  branch: Branch,
  day: ReturnType<typeof branchHours>,
  lang: StaffLanguage,
  say: (key: StaffKey, vars?: Record<string, string>) => string,
): string {
  if (day === 'none') return ''
  if (day === 'allDay') return say('entry.openAllDay')
  if (day === 'closed') {
    return branch.opensAt ? say('entry.closedOpens', { at: when(branch.opensAt, lang, branch.timezone) }) : say('entry.closed')
  }
  return branch.closesAt ? say('entry.openUntil', { at: when(branch.closesAt, lang, branch.timezone) }) : ''
}

/** The branch's own clock: "open until 18:00" is the branch's 18:00 wherever the reader is. */
function when(iso: string, lang: StaffLanguage, zone: string): string {
  return formatMoment(iso, lang, { time: true, zone })
}
